<template>
  <!-- Gift review: counter 2, the one that costs money. The system is the
       straight man; the assistant's reasons and the recipient's rulings are
       the content. Every promise here is one the backend keeps. -->
  <main class="page gift-page">
    <p v-if="loading" class="lab">Fetching the slate…</p>

    <div v-else-if="error" class="panel sober missing">
      <span class="rstamp returned">Not here</span>
      <h1 class="d3 gap">Can&#8217;t find that.</h1>
      <p class="sm">{{ error }}</p>
      <router-link to="/assistant" class="btn ghost">Back to the desk</router-link>
    </div>

    <template v-else-if="proposal">
      <header class="top">
        <div>
          <span class="lab" :class="{ red: isPending && !isStandalone }">{{ counterLabel }}</span>
          <h1 class="d2 head">{{ headline }}</h1>
          <p v-if="proposal.occasion" class="lab occ">{{ proposal.occasion }}</p>
        </div>
        <span v-if="!isPending" class="rstamp" :class="stamp.cls">{{ stamp.text }}</span>
      </header>

      <!-- Going in together (S11): answered on the desk, shown wherever the gift is -->
      <div v-if="poolNote" class="panel sober pool-note">
        <span class="lab">{{ poolNote.label }}</span>
        <p class="sm gap">{{ poolNote.text }}</p>
        <div v-if="poolNote.link" class="btnrow gap"><router-link :to="poolNote.link" class="btn ghost small">Answer on your desk →</router-link></div>
      </div>

      <!-- ── waiting on you ─────────────────────────────────────────── -->
      <template v-if="isPending">
        <!-- S17 · chosen under the envelope: notice, and the stop. -->
        <div v-if="armed" class="panel carbon envelope-notice">
          <span class="lab red">Under your envelope</span>
          <h2 class="d3 gap">Unless you stop me: {{ armed.title }}, on {{ fmtWhen(armed.ordersAt) }}.</h2>
          <p class="sm gap">
            You signed an envelope that covers {{ name }}, so I&#8217;ve chosen this one myself. Nothing is ordered or charged until then
            &mdash; up to {{ dollars(armed.holdCents) }} after that. Stop it and the ideas below are yours to pick from, or not.
          </p>
          <div class="btnrow gap">
            <button type="button" class="btn red" :disabled="submitting" @click="stopEnvelope">{{ submitting ? 'Stopping…' : 'Stop it' }}</button>
            <router-link to="/assistant/envelope" class="btn quiet">The envelope</router-link>
          </div>
        </div>
        <p v-else-if="envelopeStopped" class="sm intro envelope-said">{{ envelopeStopped }}</p>

        <p class="lede intro">{{ lede }}</p>
        <p v-if="!isStandalone" class="sm intro">
          Nothing here has been made, ordered or charged. Pick one and it&#8217;s ordered when you say so; pick none and the only
          thing that happens is I get better at this.
        </p>

        <!-- Their side: exactly what their own page ruled, and nothing about them. -->
        <div v-if="!isStandalone" class="panel sober their">
          <span class="lab" :class="theirSide.checked ? 'green' : ''">{{ theirSide.label }}</span>
          <p class="sm gap">{{ theirSide.text }}</p>
          <ul v-if="theirSide.ruledOut.length" class="ruled">
            <li v-for="(r, i) in theirSide.ruledOut" :key="i">
              <span class="struck">{{ r.title }}</span>
              <span class="xs">{{ reasonWords(r) }}</span>
            </li>
          </ul>
          <p v-if="theirSide.steer" class="sm gap steer"><span class="lab sub">Their page&#8217;s direction</span> {{ theirSide.steer }}</p>
          <p v-if="theirSide.footnote" class="xs gap">{{ theirSide.footnote }}</p>
        </div>

        <div class="slate" :class="{ one: ideas.length === 1 }">
          <article
            v-for="p in ideas"
            :key="p.candidateId"
            class="gift"
            :data-sel="selectedCandidateId === p.candidateId ? 'true' : 'false'"
          >
            <div class="lane">
              <span class="pill">{{ laneLabel(p.lane) }}</span>
              <span v-if="p.lane === 'gesture'" class="money">No cost</span>
              <span v-else-if="p.priceCents != null" class="money">{{ dollars(p.priceCents) }}</span>
            </div>

            <figure v-if="p.lane !== 'gesture' || p.imageUrl" class="obj" :class="{ blank: !p.imageUrl }">
              <img v-if="p.imageUrl" :src="p.imageUrl" :alt="p.title" loading="lazy" />
              <figcaption v-else class="lab">{{ blankCaption(p) }}</figcaption>
              <span v-if="isAgentPick(p)" class="rstamp live mine">My pick</span>
            </figure>

            <h2 class="d4">{{ p.title }}</h2>
            <p v-if="p.rationale" class="note why"><strong>Why this:</strong> {{ p.rationale }}</p>
            <p v-if="isAgentPick(p) && proposal.agentPick.why" class="sm pickwhy">
              <strong>Why I&#8217;d choose it:</strong> {{ proposal.agentPick.why }}
            </p>

            <dl class="slip">
              <div v-if="sourceWords(p)"><dt>From</dt><dd>{{ sourceWords(p) }}</dd></div>
              <div v-if="rulingWords(p)"><dt>Their side</dt><dd v-html="rulingWords(p)"></dd></div>
              <div v-if="p.url && p.source !== 'bespoke' && p.lane !== 'gesture'">
                <dt>Listing</dt><dd><a :href="p.url" target="_blank" rel="noopener noreferrer">See it at the shop</a></dd>
              </div>
            </dl>

            <button
              type="button"
              class="pick"
              :aria-pressed="selectedCandidateId === p.candidateId ? 'true' : 'false'"
              :disabled="submitting"
              @click="choose(p)"
            >
              <span class="box" aria-hidden="true"></span>{{ p.lane === 'gesture' ? 'This one' : 'Choose this one' }}
            </button>
          </article>
        </div>

        <div class="panel sober bar" aria-live="polite">
          <div class="bar-row">
            <p class="sm hint">{{ hint }}</p>
            <div class="btnrow">
              <button type="button" class="btn" :disabled="!selectedCandidateId || submitting" @click="approve">
                {{ submitting && !showDeclineForm ? 'Sending…' : approveLabel }}
              </button>
              <button v-if="!showDeclineForm" type="button" class="btn red" :disabled="submitting" @click="openDecline">
                {{ isStandalone ? 'Not this one' : 'None of these' }}
              </button>
            </div>
          </div>
          <p v-if="!isStandalone" class="xs gap">Saying none of these is a useful answer. Saying it twice is more useful still.</p>

          <div v-if="showDeclineForm" class="decline">
            <span class="lab">{{ isStandalone ? 'What’s off about it?' : 'What was wrong with them?' }}</span>
            <div class="chiprow gap">
              <button
                v-for="r in (isStandalone ? gestureDeclineReasons : declineReasons)"
                :key="r"
                type="button"
                class="chip"
                :aria-pressed="declineReason === r ? 'true' : 'false'"
                @click="declineReason = declineReason === r ? '' : r"
              ><span class="box" aria-hidden="true"></span>{{ capital(r) }}</button>
            </div>
            <label class="fl" for="decline-note">{{ isStandalone ? 'Anything else (optional)' : 'The real reason, if there is one (optional)' }}</label>
            <input
              id="decline-note"
              v-model="declineNote"
              type="text"
              maxlength="300"
              :placeholder="isStandalone ? 'We already talked last night' : 'She’d never wear jewellery'"
            />
            <div class="btnrow gap">
              <button type="button" class="btn ghost" :disabled="submitting" @click="decline">
                {{ submitting ? 'Saving…' : (isStandalone ? 'Pass on it' : 'Pass on these') }}
              </button>
              <button type="button" class="btn quiet" :disabled="submitting" @click="showDeclineForm = false">Cancel</button>
            </div>
          </div>
        </div>

        <p class="xs foot">
          Gifts are billed per item, on your yes. The membership pays for the assistant, never for what it buys.
        </p>
      </template>

      <!-- ── decided: the carbon ─────────────────────────────────────── -->
      <template v-else>
        <div class="panel carbon" :class="statusClass">
          <span class="cc">Your copy · counter 2<span v-if="decidedAt"> · {{ decidedAt }}</span></span>
          <h2 class="d3 gap">{{ statusHeading }}</h2>
          <p v-if="statusDetail" class="sm gap">{{ statusDetail }}</p>

          <div v-if="chosen" class="chosen">
            <img v-if="chosen.imageUrl" :src="chosen.imageUrl" :alt="chosen.title" />
            <div>
              <span class="lab">{{ laneLabel(chosen.lane) }}</span>
              <p class="d4">{{ chosen.title }}</p>
              <span v-if="chosen.priceCents != null && chosen.lane !== 'gesture'" class="money">{{ dollars(chosen.priceCents) }}</span>
            </div>
          </div>

          <p v-if="declinedWith" class="sm gap">You said: <span class="theirwords">{{ declinedWith }}</span></p>

          <!-- S17 · it chose this one alone: say so, and keep the undo real. -->
          <div v-if="byEnvelope" class="followup envelope-undo">
            <p class="sm"><strong>I chose this one myself,</strong> under the envelope you signed. Nobody stopped it during its notice.</p>
            <template v-if="canUndo">
              <p class="xs gap">Changed your mind? Until {{ fmtWhen(undoUntil) }} I can try to pull it back from the printer &mdash; if it hasn&#8217;t started, it&#8217;s cancelled, refunded, and they never know.</p>
              <div class="btnrow gap"><button type="button" class="btn ghost" :disabled="submitting" @click="stopEnvelope">{{ submitting ? 'Trying…' : 'Undo it' }}</button></div>
            </template>
          </div>
          <p v-if="envelopeAnswer && proposal.status !== 'undone'" class="sm gap envelope-said">{{ envelopeAnswer }}</p>

          <!-- How it went: the member's word on a gesture is the best evidence there is. -->
          <div v-if="proposal.status === 'gesture_chosen'" class="followup">
            <template v-if="proposal.followUp && !editingFollowUp">
              <p class="sm">
                You said {{ outcomeWords(proposal.followUp.outcome) }}<span v-if="proposal.followUp.note">:
                <span class="theirwords">&#8220;{{ proposal.followUp.note }}&#8221;</span></span>
              </p>
              <button type="button" class="btn quiet" @click="editingFollowUp = true">Change that</button>
            </template>
            <template v-else>
              <span class="lab">How did it go? I&#8217;ll remember what you say about {{ proposal.recipientName }}.</span>
              <div class="chiprow gap">
                <button
                  v-for="o in gestureOutcomes"
                  :key="o.value"
                  type="button"
                  class="chip"
                  :aria-pressed="followUpOutcome === o.value ? 'true' : 'false'"
                  @click="followUpOutcome = o.value"
                ><span class="box" aria-hidden="true"></span>{{ o.label }}</button>
              </div>
              <label class="fl" for="followup-note">Anything worth remembering (optional)</label>
              <input id="followup-note" v-model="followUpNote" type="text" maxlength="600" placeholder="He wants to go fishing in May" />
              <div class="btnrow gap">
                <button type="button" class="btn" :disabled="!followUpOutcome || submitting" @click="sendFollowUp">
                  {{ submitting ? 'Saving…' : 'Tell my assistant' }}
                </button>
              </div>
            </template>
          </div>

          <!-- Did it land (S13): theirs first, from their own page; then what you heard. -->
          <div v-if="landable" class="followup landing">
            <p v-if="landing.them" class="sm">
              <strong>{{ firstName }} said, on their own page:</strong> {{ landingWords(landing.them.verdict) }}<span v-if="landing.them.note">:
              <span class="theirwords">&#8220;{{ landing.them.note }}&#8221;</span></span>
            </p>
            <template v-if="landing.member && !editingLanding">
              <p class="sm">You said: {{ landingWords(landing.member.verdict) }}<span v-if="landing.member.note">:
                <span class="theirwords">&#8220;{{ landing.member.note }}&#8221;</span></span></p>
              <button type="button" class="btn quiet" @click="editingLanding = true">Change that</button>
            </template>
            <template v-else-if="!landing.them || editingLanding">
              <span class="lab">{{ arrived ? `Did it land? I'd rather know that than count what went out.` : `On its way. Once it's arrived, tell me how it landed.` }}</span>
              <template v-if="arrived || editingLanding">
                <div class="chiprow gap">
                  <button v-for="o in landingOptions" :key="o.value" type="button" class="chip"
                    :aria-pressed="landingVerdictDraft === o.value ? 'true' : 'false'" @click="landingVerdictDraft = o.value">
                    <span class="box" aria-hidden="true"></span>{{ o.label }}
                  </button>
                </div>
                <label class="fl" for="landing-note">What you heard or saw (optional)</label>
                <input id="landing-note" v-model="landingNote" type="text" maxlength="400" placeholder="She sent a photo of it on the wall" />
                <div class="btnrow gap">
                  <button type="button" class="btn" :disabled="!landingVerdictDraft || submitting" @click="sendLanding">
                    {{ submitting ? 'Saving…' : 'Tell my assistant' }}
                  </button>
                </div>
              </template>
            </template>
          </div>

          <div class="btnrow actions">
            <a v-if="manualBuyUrl" :href="manualBuyUrl" target="_blank" rel="noopener noreferrer" class="btn">Finish it at the shop</a>
            <button v-if="canRetry" type="button" class="btn" :disabled="submitting" @click="retry">
              {{ submitting ? 'Trying…' : 'Try again' }}
            </button>
            <button v-if="canRetry" type="button" class="btn ghost" :disabled="submitting" @click="openBilling">Update your card</button>
            <router-link to="/assistant" class="btn" :class="manualBuyUrl || canRetry ? 'quiet' : 'ghost'">Back to the desk</router-link>
          </div>
        </div>
      </template>
    </template>
  </main>
</template>

<script>
import { db, auth } from "@/firebase";
import { doc, onSnapshot } from "firebase/firestore";
import { decideGiftProposal, reportGestureOutcome, retryGiftPurchase, createCustomerPortalLink, reportLanding, stopEnvelopeGift } from "@/api";
import { MEMBERSHIP_PRICE_DISPLAY } from "@/config/membership";

const GESTURE_OUTCOMES = [
  { value: "went_well", label: "It went well", words: "it went well" },
  { value: "mixed", label: "Mixed", words: "it was mixed" },
  { value: "didnt_happen", label: "It didn't happen", words: "it didn't happen" },
];

// Mirrors MEMBER_LANDING in functions/shared/recipient/landing.js.
const LANDING_OPTIONS = [
  { value: "loved", label: "They loved it", words: "they loved it" },
  { value: "liked", label: "It went down fine", words: "it went down fine" },
  { value: "missed", label: "Honestly, it missed", words: "it missed" },
  { value: "unheard", label: "I haven't heard", words: "you haven't heard yet" },
];

const LANE_LABELS = {
  practical: "Practical",
  sentimental: "Sentimental",
  experience: "Experience",
  bespoke: "Made for them",
  gesture: "Not a thing",
};

const NUMBER_WORDS = ["No", "One", "Two", "Three", "Four", "Five", "Six"];

// The recipient's agent rules with coded reasons, already gated by their
// disclosure level (negotiate.js). Each one in words, saying no more than the
// code does. A missing reason on "unavailable" is the recipient choosing not
// to say why, so we don't either.
const REASON_WORDS = {
  owned: "they already have one",
  duplicate_incoming: "someone else is already sending one",
  constraint: "against something they asked senders to know",
  timing: "bad timing, by their page",
  taste: "not their taste, by their page",
  policy: "their page isn't taking gifts right now",
  undisclosed: "their page didn't say why",
};

const REFUSAL_WORDS = {
  not_negotiable: "doesn't rule on gift ideas",
  rounds_exhausted: "has answered enough questions for now",
  enumeration: "won't answer a list that long",
};

export default {
  name: "GiftReviewPage",
  data() {
    return {
      proposal: null,
      loading: true,
      submitting: false,
      error: null,
      selectedCandidateId: null,
      unsubscribe: null,
      showDeclineForm: false,
      declineReason: "",
      declineNote: "",
      declineReasons: ["too generic", "wrong vibe", "they have one", "too pricey", "bad timing"],
      gestureDeclineReasons: ["already did", "not how we are", "bad timing"],
      gestureOutcomes: GESTURE_OUTCOMES,
      followUpOutcome: "",
      followUpNote: "",
      editingFollowUp: false,
      landingOptions: LANDING_OPTIONS,
      landingVerdictDraft: "",
      landingNote: "",
      editingLanding: false,
      envelopeAnswer: "",
    };
  },
  computed: {
    name() {
      return (this.proposal && this.proposal.recipientName) || "them";
    },
    ideas() {
      return (this.proposal && this.proposal.proposals) || [];
    },
    // A gesture the agent proposed on its own: no slate, one thing to do.
    isStandalone() {
      return Boolean(this.proposal && this.proposal.kind === "gesture");
    },
    // S17: chosen under the member's envelope and waiting out its notice.
    armed() {
      const p = this.proposal;
      return p && p.autonomyState === "noticed" && p.autonomy ? p.autonomy : null;
    },
    envelopeStopped() {
      const a = this.proposal && this.proposal.autonomy;
      if (!a) return "";
      if (a.state === "stopped") return `You stopped my envelope pick (${a.title}). Nothing was ordered or charged.`;
      if (a.state === "declined") return `Not one I'd order on my own: ${a.words}`;
      if (a.state === "lapsed") return `I was going to order ${a.title} under your envelope, but didn't. It's your call.`;
      return "";
    },
    byEnvelope() {
      const p = this.proposal;
      return Boolean(p && p.decision && p.decision.by === "mandate" && ["purchased", "purchasing", "approved"].includes(p.status));
    },
    undoUntil() {
      const a = this.proposal && this.proposal.autonomy;
      return a && a.orderedAt ? a.orderedAt + 48 * 60 * 60 * 1000 : null;
    },
    canUndo() {
      const p = this.proposal;
      return Boolean(p && p.status === "purchased" && p.autonomyState === "ordered" && this.undoUntil && this.undoUntil > Date.now() && !this.envelopeAnswer);
    },
    isPending() {
      return this.proposal && this.proposal.status === "pending_approval";
    },
    counterLabel() {
      if (!this.isPending) return "Counter 2 · decided";
      if (this.isStandalone) return "Counter 2 · costs nothing · waiting on you";
      return "Counter 2 · costs money · waiting on you";
    },
    headline() {
      if (!this.isPending) {
        const s = this.proposal.status;
        if (s === "declined") return `Nothing for ${this.name}, this time.`;
        if (this.chosen && s !== "purchase_failed") return `For ${this.name}: ${this.lowerFirst(this.chosen.title)}.`;
        return `For ${this.name}.`;
      }
      if (this.isStandalone) return `Something for you to do, for ${this.name}.`;
      const n = this.ideas.length;
      if (n === 1) return `One thing for ${this.name}.`;
      return `${NUMBER_WORDS[n] || n} things for ${this.name}.`;
    },
    leadIsGesture() {
      return Boolean(this.ideas[0] && this.ideas[0].lane === "gesture");
    },
    lede() {
      if (this.isStandalone) {
        return "This one isn't mine to send. It's yours to do, and it will mean more than anything I could mail.";
      }
      if (this.leadIsGesture) {
        return `Honestly? I don't think ${this.name} needs a thing right now. Here's what I'd do instead, ` +
          "and a few ideas if you'd still rather send something.";
      }
      if (this.proposal.agentPick) return "The one I'd choose comes first and is marked. The others are there because they're close.";
      return "Ranked by how much each one leans on something true about them, not by price.";
    },
    // What the member may know of the recipient's side, from what the proposal
    // recorded (theirSide, S15) or, on older proposals, only that a consult ran.
    landable() {
      const p = this.proposal || {};
      return p.status === "purchased" || (p.status === "pooled" && this.joint.status === "ordered");
    },
    landing() {
      return (this.proposal && this.proposal.landing) || {};
    },
    arrived() {
      const at = this.proposal && this.proposal.arrivesAt;
      return !at || at <= Date.now();
    },
    firstName() {
      return String((this.proposal && this.proposal.recipientName) || "They").trim().split(/\s+/)[0];
    },
    joint() {
      return (this.proposal && this.proposal.pool && this.proposal.pool.joint) || {};
    },
    poolWith() {
      return (this.proposal && this.proposal.pool && this.proposal.pool.with) || "the other member";
    },
    poolNote() {
      const pool = this.proposal && this.proposal.pool;
      if (!pool) return null;
      const first = String(this.proposal.recipientName || '').trim().split(/\s+/)[0] || 'them';
      const occ = (pool.occasion && pool.occasion.type) || 'occasion';
      if (pool.state === 'offered' && pool.role === 'offeree' && pool.expiresAt > Date.now()) {
        return {
          label: 'Going in together?',
          text: `Someone else is getting ${first} something for their ${occ} too, and would go in with you. I won\u2019t say who unless you both say yes.`,
          link: `/assistant?pool=${pool.poolId}`,
        };
      }
      if (pool.state === 'formed') {
        const j = pool.joint || {};
        const both = `You and ${pool.with} are both getting ${first} something for their ${occ}.`;
        if (j.status === 'building') return { label: 'Going in together', text: `${both} I\u2019m looking for something you could give together.` };
        if (j.status === 'ready') {
          return {
            label: 'Going in together',
            text: `${both} There ${j.options.length === 1 ? 'is one idea' : `are ${j.options.length} ideas`} you could give together, with your share, on your desk. Choosing one of these instead ends that.`,
            link: `/assistant?pool=${pool.poolId}`,
          };
        }
        if (['agreed', 'ordering', 'ordered'].includes(j.status)) return null; // the status line says it
        return { label: 'Going in together', text: `${both} You each keep your own gift; I\u2019ll keep them from being the same kind of thing.` };
      }
      if (pool.state === 'dissolved' && pool.joint && pool.joint.status === 'failed') {
        return {
          label: 'Going in together',
          text: pool.joint.reason === 'your_card' ?
            'Your card was declined, so the gift you chose together wasn\u2019t ordered and nobody was charged. Your own ideas are back, here.' :
            'The gift you chose together couldn\u2019t be ordered, and nobody was charged. Your own ideas are back, here.',
        };
      }
      return null;
    },
    theirSide() {
      const p = this.proposal || {};
      const side = p.theirSide;
      const consulted = Boolean(p.provenance && p.provenance.consultId);
      if (side && side.refused) {
        const why = REFUSAL_WORDS[side.refused] || "declined to rule this time";
        return {
          checked: false, label: "Not checked", ruledOut: [],
          text: `${this.name}'s own page ${why}, so nothing here was checked against it. It's your read and mine.`,
        };
      }
      if (side) {
        const ruledOut = side.ruledOut || [];
        const asked = side.asked || 0;
        const text = `${this.name}'s own page was asked about ${asked} idea${asked === 1 ? "" : "s"} before you saw any of them. ` +
          (ruledOut.length ?
            `It ruled out ${ruledOut.length === 1 ? "one" : ruledOut.length}, so you won't see ${ruledOut.length === 1 ? "it" : "them"}:` :
            "It ruled none of them out.");
        return {
          checked: true, label: `Checked against ${this.name}'s side first`, ruledOut, text,
          steer: side.steer || null,
          footnote: side.steer ?
            "Their page chose that direction from a short fixed list. It can't say more than that, and it isn't them choosing." :
            "A yes from their page means nothing it knows objects. It isn't them choosing.",
        };
      }
      if (consulted) {
        return {
          checked: true, label: `Checked against ${this.name}'s side first`, ruledOut: [],
          text: `${this.name}'s own page was asked about these before you saw them. What it ruled out never reached this slate.`,
        };
      }
      return {
        checked: false, label: "Not checked", ruledOut: [],
        text: `Nothing here was checked against ${this.name}'s side. It's your read and mine.`,
      };
    },
    chosen() {
      if (!this.proposal || !this.proposal.decision) return null;
      return this.ideas.find((p) => p.candidateId === this.proposal.decision.candidateId) || null;
    },
    declinedWith() {
      const d = this.proposal && this.proposal.decision;
      if (!d || d.choice !== "decline") return "";
      return [d.reason, d.note].filter(Boolean).join(" — ");
    },
    decidedAt() {
      const d = this.proposal && this.proposal.decision;
      const at = (d && (d.at || d.decidedAt)) || (this.proposal && this.proposal.updatedAt);
      if (!at) return "";
      return new Date(at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }).toLowerCase();
    },
    statusClass() {
      const s = this.proposal && this.proposal.status;
      if (s === "purchased" || s === "gesture_chosen" || s === "pooled") return "green";
      if (s === "purchase_failed") return "red";
      return "";
    },
    stamp() {
      switch (this.proposal && this.proposal.status) {
        case "purchased": return { cls: "filed", text: "Bought" };
        case "purchase_failed": return { cls: "held", text: "Not ordered" };
        case "declined": return { cls: "returned", text: "Passed" };
        case "gesture_chosen": return { cls: "filed", text: "Yours to do" };
        case "pooled": return { cls: "filed", text: "Together" };
        case "undone": return { cls: "returned", text: "Undone" };
        case "purchasing": return { cls: "live", text: "Ordering" };
        default: return { cls: "live", text: "Approved" };
      }
    },
    isBespokePick() {
      return this.chosen && this.chosen.source === "bespoke";
    },
    // The manual rail: auto-purchase wasn't available (or failed), so the
    // approved pick is completed by the sender at the merchant. Set server-side
    // as purchase.rail === "manual" with the listing url.
    manualBuyUrl() {
      const p = this.proposal;
      if (!p || !p.purchase || p.purchase.rail !== "manual") return null;
      return p.purchase.url || (this.chosen && this.chosen.url) || null;
    },
    // Bespoke printing isn't configured on the server yet: the pick stays
    // approved and nothing is made or charged until it is.
    printingUnavailable() {
      const p = this.proposal;
      return Boolean(p && p.purchase && p.purchase.rail === "unavailable");
    },
    // A billing failure is the member's to retry once they've fixed it; the
    // server allows three attempts in all (applyRetry).
    canRetry() {
      const p = this.proposal;
      return Boolean(p && p.status === "purchase_failed" && p.purchase && p.purchase.rail === "billing" && (p.attempt || 1) < 3);
    },
    cardDeclined() {
      const p = this.proposal;
      return Boolean(p && p.purchase && p.purchase.rail === "billing");
    },
    selectedPick() {
      return this.ideas.find((c) => c.candidateId === this.selectedCandidateId) || null;
    },
    selectedPriceLabel() {
      const picked = this.selectedPick;
      return picked && picked.priceCents != null ? this.dollars(picked.priceCents) : "";
    },
    // Something we make ourselves is our price, exactly. Something bought from
    // a shop has the shop's shipping and tax on top, which nobody knows until
    // checkout — so that one is quoted as a range, not a number.
    selectedIsBespoke() {
      const picked = this.selectedPick;
      return Boolean(picked && (picked.source === "bespoke" || (picked.attributes && picked.attributes.bespoke)));
    },
    selectedMaxLabel() {
      const picked = this.selectedPick;
      if (!picked || picked.priceCents == null || this.selectedIsBespoke) return "";
      // Mirrors catalogSpendCeilingCents() in manufactureApprovedGift.js — the
      // most we will ever let a catalog order cost, and the amount held.
      const ceiling = Math.min(Math.ceil(picked.priceCents * 1.25) + 2000, 20000);
      return this.dollars(ceiling);
    },
    selectedIsGesture() {
      return Boolean(this.selectedPick && this.selectedPick.lane === "gesture");
    },
    approveLabel() {
      if (!this.selectedPick) return "Approve — pick one first";
      if (this.selectedIsGesture) return "I'll do this";
      // A button that charges money says the amount.
      if (!this.selectedPriceLabel) return "Approve and send";
      const price = this.selectedIsBespoke ? this.selectedPriceLabel : `about ${this.selectedPriceLabel}`;
      return `Approve and buy — ${price}`;
    },
    hint() {
      const picked = this.selectedPick;
      if (!picked) return this.isStandalone ? "Nothing is bought. This one is yours to do." : "Nothing is selected. Nothing is charged.";
      if (this.selectedIsGesture) return `Selected: ${picked.title}. Nothing is ordered or charged.`;
      if (!this.selectedPriceLabel) return `Selected: ${picked.title}.`;
      if (this.selectedIsBespoke) {
        return `Selected: ${picked.title}. ${this.selectedPriceLabel}, charged to the card on your membership once it's made — ` +
          `not part of the ${this.membershipPrice}/month.`;
      }
      return `Selected: ${picked.title}. ${this.selectedPriceLabel} plus the shop's shipping and tax: we hold up to ` +
        `${this.selectedMaxLabel} and charge only what it comes to, once it's ordered. Not part of the ${this.membershipPrice}/month.`;
    },
    membershipPrice() {
      return MEMBERSHIP_PRICE_DISPLAY;
    },
    statusHeading() {
      switch (this.proposal && this.proposal.status) {
        case "approved":
          if (this.printingUnavailable) return "Kept — printing isn't set up yet.";
          return this.manualBuyUrl ? "Approved. One more step is yours." : "Approved.";
        case "purchasing": return this.isBespokePick ? "Making it now." : "Ordering it now.";
        case "purchased": return `On its way to ${this.name}.`;
        case "purchase_failed": return this.cardDeclined ? "Your card was declined." : "That didn't go through.";
        case "declined": return "Nothing bought.";
        case "gesture_chosen": return this.isStandalone ? "It's yours to do." : "Nothing to buy.";
        case "pooled": return this.joint.status === "ordered" ? `From you and ${this.poolWith}.` : "Going in together instead.";
        case "undone": return "Undone.";
        default: return "Done.";
      }
    },
    statusDetail() {
      const title = this.chosen && this.chosen.title;
      switch (this.proposal && this.proposal.status) {
        case "approved":
          if (this.printingUnavailable) {
            return "Your pick is kept. Nothing has been made or charged — printing isn't set up on our side yet.";
          }
          if (this.manualBuyUrl) {
            return `Ordering isn't automatic for ${title ? `"${title}"` : "this one"}. Finish it at the shop and it ships straight to them.`;
          }
          return title ? `"${title}" is being got ready.` : "Your pick is being got ready.";
        case "purchasing":
          return this.isBespokePick ?
            "Generating and ordering the one-of-a-kind piece. This takes a moment." :
            "Placing the order with the shop. This can take a few minutes.";
        case "purchased": return title ? `"${title}" is ordered and on its way.` : "Ordered and on its way.";
        case "purchase_failed":
          if (this.cardDeclined) {
            if (!this.canRetry) {
              return "Nothing was ordered and nothing was charged. It's been tried three times, so this one is closed — " +
                "I'll find the next chance.";
            }
            return "Nothing was ordered and nothing was charged. Update your card, then try again — it picks up where it left off.";
          }
          return this.manualBuyUrl ?
            "Automatic ordering didn't go through and nothing was charged. You can still finish it at the shop." :
            "It couldn't be completed and nothing was charged. We'll be in touch.";
        case "declined":
          if (this.isStandalone) return "Got it. I won't suggest that one again.";
          return `Nothing was charged, and none of these will come back. I'll watch for the next good moment for ${this.name}.`;
        case "gesture_chosen":
          if (this.proposal.followUp) return "";
          return "Nothing was ordered or charged. Come back and tell me how it went.";
        case "undone":
          return `${this.chosen ? `"${this.chosen.title}"` : "It"} was cancelled before it was made and the money went back on your card. ${this.name} never knew it was coming.`;
        case "pooled": {
          const together = (this.joint.options || []).find((o) => o.candidateId === this.joint.chosen);
          const what = together ? `"${together.title}"` : "the gift you chose together";
          if (this.joint.status === "ordered") return `You and ${this.poolWith} gave ${what}. Your share was $${((this.joint.myChargeCents || 0) / 100).toFixed(2)}; none of these was bought.`;
          return `You and ${this.poolWith} both chose ${what}, and it's being ordered with your share on your card. These wait here; if that falls through, they come back exactly as they were.`;
        }
        default: return "";
      }
    },
  },
  mounted() {
    const id = this.$route.params.id;
    if (!id) {
      this.error = "No gift specified.";
      this.loading = false;
      return;
    }
    // Live subscription so the status moves approved -> making -> on its way.
    this.unsubscribe = onSnapshot(
      doc(db, "giftProposals", id),
      (snap) => {
        this.loading = false;
        if (!snap.exists()) {
          this.error = "This gift proposal no longer exists.";
          return;
        }
        const data = snap.data();
        if (auth.currentUser && data.userId !== auth.currentUser.uid) {
          this.error = "This gift isn't yours to review.";
          this.proposal = null;
          return;
        }
        this.error = null;
        this.proposal = { id: snap.id, ...data };
        // One thing to do: nothing to choose between.
        if (data.kind === "gesture" && !this.selectedCandidateId && (data.proposals || []).length === 1) {
          this.selectedCandidateId = data.proposals[0].candidateId;
        }
      },
      () => {
        this.loading = false;
        this.error = "We couldn't load this gift (you may need to sign in).";
      },
    );
  },
  beforeUnmount() {
    if (this.unsubscribe) this.unsubscribe();
  },
  methods: {
    dollars(cents) {
      return `$${(cents / 100).toFixed(2)}`;
    },
    lowerFirst(s) {
      return s ? s.charAt(0).toLowerCase() + s.slice(1) : s;
    },
    capital(s) {
      return s.charAt(0).toUpperCase() + s.slice(1);
    },
    laneLabel(lane) {
      return LANE_LABELS[lane] || "Gift";
    },
    blankCaption(p) {
      if (p.lane === "gesture") return "Not a thing to post";
      if (p.source === "bespoke") return "Made after you say yes";
      return "No picture from the shop";
    },
    sourceWords(p) {
      if (p.lane === "gesture") return "";
      if (p.source === "bespoke") return "Made for them, printed after your yes";
      if (p.source === "etsy") return "A shop on Etsy";
      return "";
    },
    rulingWords(p) {
      if (p.ruling === "yes" && p.fits) return "<b>Their page likes this one.</b> It fits something they said they&#8217;re into.";
      if (p.ruling === "yes") return "<b>Cleared.</b> Nothing their page knows objects.";
      if (p.ruling === "soft_no" && p.because === "taste") return "<b>Not quite their taste</b>, says their page. Still possible, so it&#8217;s here — at the back.";
      if (p.ruling === "soft_no") return "<b>Would rather not.</b> Still possible, so it&#8217;s here — at the back.";
      return "";
    },
    reasonWords(r) {
      if (r.because && REASON_WORDS[r.because]) return REASON_WORDS[r.because];
      if (r.ruling === "unavailable") return "not available from them right now";
      return "ruled out";
    },
    isAgentPick(p) {
      const pick = this.proposal && this.proposal.agentPick;
      return Boolean(pick && pick.candidateId === p.candidateId);
    },
    choose(p) {
      this.selectedCandidateId = p.candidateId;
      this.showDeclineForm = false;
    },
    openDecline() {
      this.showDeclineForm = true;
      if (!this.isStandalone) this.selectedCandidateId = null;
    },
    async approve() {
      if (!this.selectedCandidateId) {
        this.$toast.error("Pick one first.");
        return;
      }
      this.submitting = true;
      try {
        await decideGiftProposal({
          proposalId: this.proposal.id,
          choice: "approve",
          candidateId: this.selectedCandidateId,
        });
        // onSnapshot reflects the new status (approved -> purchasing -> ...).
      } catch (e) {
        this.$toast.error(this.friendlyError(e));
      } finally {
        this.submitting = false;
      }
    },
    async decline() {
      this.submitting = true;
      try {
        await decideGiftProposal({
          proposalId: this.proposal.id,
          choice: "decline",
          reason: this.declineReason || undefined,
          note: this.declineNote.trim() || undefined,
        });
        this.showDeclineForm = false;
      } catch (e) {
        this.$toast.error(this.friendlyError(e));
      } finally {
        this.submitting = false;
      }
    },
    async retry() {
      this.submitting = true;
      try {
        await retryGiftPurchase({ proposalId: this.proposal.id });
      } catch (e) {
        this.$toast.error(this.friendlyError(e));
      } finally {
        this.submitting = false;
      }
    },
    async openBilling() {
      this.submitting = true;
      try {
        const { data } = await createCustomerPortalLink({ returnUrl: window.location.href });
        window.location.href = data.url;
      } catch (e) {
        this.$toast.error("Couldn't open billing — please try again.");
        this.submitting = false;
      }
    },
    landingWords(value) {
      if (value === "loved") return "loved it";
      if (value === "liked") return "liked it";
      const o = LANDING_OPTIONS.find((x) => x.value === value);
      return o ? o.words : value;
    },
    async sendLanding() {
      this.submitting = true;
      try {
        await reportLanding({ proposalId: this.proposal.id, verdict: this.landingVerdictDraft, note: this.landingNote.trim() || undefined });
        this.editingLanding = false;
        this.landingVerdictDraft = "";
        this.landingNote = "";
      } catch (e) {
        this.$toast.error(this.friendlyError(e));
      } finally {
        this.submitting = false;
      }
    },
    // S17: one button for both — stop during the notice, undo after the order.
    async stopEnvelope() {
      this.submitting = true;
      try {
        const { data } = await stopEnvelopeGift({ proposalId: this.proposal.id });
        this.envelopeAnswer = data.words;
        if (data.stopped) this.$toast.success(data.words);
      } catch (e) {
        this.$toast.error(this.friendlyError(e));
      } finally {
        this.submitting = false;
      }
    },
    fmtWhen(ms) {
      return ms ? new Date(ms).toLocaleString(undefined, { weekday: "long", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";
    },
    outcomeWords(value) {
      const o = GESTURE_OUTCOMES.find((x) => x.value === value);
      return o ? o.words : value;
    },
    async sendFollowUp() {
      this.submitting = true;
      try {
        await reportGestureOutcome({
          proposalId: this.proposal.id,
          outcome: this.followUpOutcome,
          note: this.followUpNote.trim() || undefined,
        });
        this.editingFollowUp = false;
      } catch (e) {
        this.$toast.error(this.friendlyError(e));
      } finally {
        this.submitting = false;
      }
    },
    friendlyError(e) {
      const code = e && e.code ? String(e.code) : "";
      if (code.includes("failed-precondition")) return "This was already decided.";
      if (code.includes("permission-denied")) return "This gift isn't yours.";
      return "Something went wrong — try again.";
    },
  },
};
</script>

<style scoped>
.envelope-notice { margin: 18px 0 8px; border-color: var(--red); }
.envelope-undo { border-top: 1px solid var(--rule); padding-top: 12px; }
.envelope-said { color: var(--ink-2); }
.gift-page { width: 100%; box-sizing: border-box; }
.top { display: flex; flex-wrap: wrap; gap: 14px 24px; align-items: center; }
.top .rstamp { margin-left: auto; }
.head { margin-top: 10px; max-width: 22ch; }
.occ { margin-top: 10px; color: var(--ink-3); }
.intro { max-width: 64ch; margin-top: 14px; }
.gap { margin-top: 10px; }
.missing { max-width: 34rem; }
.missing .btn { margin-top: 16px; }

.their { margin-top: 24px; max-width: 72ch; }
.their .steer .lab { display: block; margin-bottom: 2px; }
.pool-note { margin-bottom: 16px; }
.ruled { list-style: none; margin: 10px 0 0; padding: 0; }
.ruled li { display: flex; flex-wrap: wrap; gap: 4px 12px; align-items: baseline; padding: 7px 0; border-top: 1px solid var(--rule); }
.struck { text-decoration: line-through; text-decoration-color: var(--green); text-decoration-thickness: 1.5px; color: var(--ink-2); }

/* the slate */
.slate { display: grid; gap: clamp(22px, 3vw, 36px); margin-top: 30px; }
@media (min-width: 660px) { .slate { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (min-width: 1020px) { .slate { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
.slate.one { max-width: 420px; grid-template-columns: 1fr; }

.gift {
  border: 1px solid var(--rule-2); background: var(--sheet); padding: 18px 18px 0;
  display: flex; flex-direction: column; transition: border-color .16s, box-shadow .16s;
}
.gift:hover { border-color: var(--ink-3); }
.gift[data-sel="true"] { border-color: var(--ink); box-shadow: 0 0 0 1.5px var(--ink); }
.lane { display: flex; justify-content: space-between; gap: 10px; align-items: center; margin-bottom: 14px; }

.obj {
  position: relative; margin: 0 0 16px; background: var(--sunk); box-shadow: inset 0 0 0 1px var(--rule);
  aspect-ratio: 4 / 3; display: grid; place-items: center; overflow: hidden;
}
.obj img { width: 100%; height: 100%; object-fit: cover; display: block; }
.obj.blank { background: repeating-linear-gradient(-45deg, var(--sunk) 0 9px, var(--kraft) 9px 18px); }
.obj figcaption { background: var(--kraft); padding: 6px 10px; border: 1px solid var(--rule-2); }
.mine { position: absolute; top: 10px; right: 10px; background: var(--sheet); font-size: .6rem; padding: 4px 8px; }

.why { margin: 12px 0 0; }
.lane + h2 { margin-top: 4px; }
.pickwhy { margin: 10px 0 0; }

.slip { margin: 14px 0 16px; display: grid; grid-template-columns: auto 1fr; border-top: 1px solid var(--rule); }
.slip > div { display: contents; }
.slip dt {
  font-family: var(--mono); font-size: .62rem; letter-spacing: .14em; text-transform: uppercase; color: var(--ink-3);
  padding: 8px 14px 7px 0; border-bottom: 1px solid var(--rule);
}
.slip dd { margin: 0; padding: 7px 0; border-bottom: 1px solid var(--rule); font-size: .85rem; color: var(--ink-2); }
.slip dd :deep(b) { font-weight: 600; color: var(--ink); }

.pick {
  appearance: none; margin: auto -18px 0; width: calc(100% + 36px); border: 0; border-top: 1px solid var(--rule);
  border-radius: 0; padding: 13px 18px; display: flex; gap: 10px; align-items: center; background: none; cursor: pointer;
  text-align: left; font-family: var(--mono); font-size: .67rem; letter-spacing: .14em; text-transform: uppercase; color: var(--ink-3);
}
.pick:hover { background: var(--sunk); color: var(--ink); }
.pick:focus-visible { outline: 2.5px solid var(--red); outline-offset: -2.5px; }
.gift[data-sel="true"] .pick { background: var(--ink); color: var(--sheet); }
.pick .box { width: 11px; height: 11px; border: 1px solid currentColor; border-radius: 50%; flex: none; }
.gift[data-sel="true"] .pick .box { background: var(--sheet); box-shadow: inset 0 0 0 2px var(--ink); }

/* the bar */
.bar { margin-top: 28px; }
.bar-row { display: flex; flex-wrap: wrap; gap: 16px; justify-content: space-between; align-items: center; }
.hint { margin: 0; flex: 1 1 22rem; max-width: 62ch; }
.decline { margin-top: 20px; border-top: 1px solid var(--rule); padding-top: 18px; }
.decline label.fl { margin-top: 16px; }
.foot { margin-top: 20px; max-width: 70ch; }

/* the carbon */
.carbon { max-width: 46rem; margin-top: 26px; border-style: dashed; }
.carbon.green { border-color: var(--green); }
.carbon.red { border-color: var(--red-ink); }
.cc { font-family: var(--mono); font-size: .62rem; letter-spacing: .16em; text-transform: uppercase; color: var(--ink-3); }
.chosen { display: flex; gap: 16px; align-items: center; margin-top: 18px; padding-top: 16px; border-top: 1px solid var(--rule); }
.chosen img { width: 84px; height: 84px; object-fit: cover; flex: none; box-shadow: 0 0 0 1px var(--rule-2); }
.chosen .d4 { margin: 4px 0; }
.followup { margin-top: 20px; padding-top: 16px; border-top: 1px solid var(--rule); }
.followup label.fl { margin-top: 14px; }
.actions { margin-top: 22px; }
</style>
